import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  message,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Table,
} from 'antd';
import { useEffect, useState } from 'react';
import { getClient } from '../../apollo';
import {
  useDrivingCompanyQuery,
  useGetAllDriversQuery,
  useUpdateDriverMutation,
} from '../../graphql/datamodel.gen';
import UpsertDriverDrawer from './components/upsertDriverDrawer';

type ManageProps = {
  visible: boolean;
  drivingCompanyId?: string;
  onClose: () => any;
};

export const Manage: React.FC<ManageProps> = (props: ManageProps) => {
  const chooseDriverSelectRef = React.useRef();
  const [updateUserForm] = Form.useForm();
  const [assignDriverForm] = Form.useForm();
  const [showUpsertUserDrawer, setShowUpsertUserDrawer] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<
    string | undefined
  >();

  const updateDriverMutation = useUpdateDriverMutation({
    client: getClient(),
  });

  const drivingCompanyQuery = useDrivingCompanyQuery({
    client: getClient(),
    variables: {
      id: props.drivingCompanyId,
      fetchDrivers: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: !props.drivingCompanyId,
  });

  const driversQuery = useGetAllDriversQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    variables: {
      fetchDrivingCompany: true,
    },
  });

  useEffect(() => {
    updateUserForm.resetFields();
  }, [props.drivingCompanyId, updateUserForm]);

  const _onClose = () => {
    updateUserForm.resetFields();
    props.onClose();
  };

  const drivingCompany = drivingCompanyQuery.data?.drivingCompany;

  console.log('drivingCompany.drivers', drivingCompany?.drivers);

  const onAssignDriver = (values) => {
    const { driver: driverID } = values;

    const driver = driversQuery.data?.drivers?.find(
      (driver) => driver.id === driverID,
    );

    const [updateDriverMut] = updateDriverMutation;

    message.loading('Asignando conductor a empresa..');

    updateDriverMut({
      variables: {
        id: driver.id,
        drivingCompanyId: drivingCompany.id,
      },
      onCompleted: async () => {
        await drivingCompanyQuery.refetch({
          id: drivingCompany.id,
          fetchDrivers: true,
        });
        assignDriverForm.resetFields(['driver']);
        message.success('Conductor asignado correctamente');
        setSelectedDriverId(undefined);
      },
    });
  };

  const drivers = driversQuery.data?.drivers?.filter((driver) => {
    return (
      driver.type === 'collaborator' &&
      (!driver.drivingCompany ||
        driver.drivingCompany?.id === drivingCompany?.id)
    );
  });

  const onClickCreateDriver = () => {
    setShowUpsertUserDrawer(true);
  };

  React.useEffect(() => {
    if (props.visible) {
      driversQuery.refetch({
        fetchDrivingCompany: true,
      });
      drivingCompanyQuery.refetch({
        fetchDrivers: true,
        id: props.drivingCompanyId,
      });
    }
  }, [props.visible]);

  React.useEffect(() => {
    if (selectedDriverId) {
      setShowUpsertUserDrawer(true);
    }
  }, [selectedDriverId]);

  const onUnassignDriver = (driverId) => {
    const [update] = updateDriverMutation;

    update({
      client: getClient(),
      variables: {
        id: driverId,
        drivingCompanyId: null,
      },
      onCompleted: () => {
        message.success('Conductor desasignado correctamente');
      },
    });
  };

  return (
    <Drawer
      closable
      onClose={_onClose}
      destroyOnClose={true}
      forceRender
      maskClosable={true}
      visible={props.visible}
      width={'50%'}
      {...(drivingCompany && {
        title: `Empresa colaboradora ${drivingCompany.name}`,
      })}
    >
      {drivingCompanyQuery?.loading && !drivingCompany && <Skeleton active />}
      {drivingCompany && (
        <>
          <Descriptions title="Información básica" column={1} size="small">
            <Descriptions.Item label="Email de empresa">
              {drivingCompanyQuery.data?.drivingCompany?.email}
            </Descriptions.Item>
            <Descriptions.Item label="Teléfono de empresa">
              {drivingCompanyQuery.data?.drivingCompany?.phone}
            </Descriptions.Item>
          </Descriptions>
          <Divider />
          <Space direction="vertical">
            <h3>Conductores</h3>
            <Space
              direction={'vertical'}
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              {!drivingCompany.drivers?.length && (
                <p>Esta empresa no tiene conductores</p>
              )}
              <Form
                form={assignDriverForm}
                layout={'inline'}
                onFinish={onAssignDriver}
              >
                <Form.Item
                  name={'driver'}
                  required={true}
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, selecciona un conductor',
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    style={{
                      minWidth: '300px',
                    }}
                    ref={chooseDriverSelectRef}
                    optionFilterProp="children"
                    dropdownRender={(menu) => {
                      return (
                        <>
                          {menu}
                          <Divider style={{ marginTop: 8, marginBottom: 4 }} />

                          <Button
                            block
                            type={'text'}
                            icon={<PlusOutlined translate={undefined} />}
                            onClick={onClickCreateDriver}
                          >
                            Crear conductor colaborador
                          </Button>
                        </>
                      );
                    }}
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '')
                        .toLowerCase()
                        .localeCompare((optionB?.label ?? '').toLowerCase())
                    }
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={drivers
                      ?.filter((driver) => {
                        return !drivingCompany.drivers.find(
                          (currentDriver) => currentDriver.id === driver.id,
                        );
                      })
                      .map((driver) => {
                        return {
                          value: driver.id,
                          label: driver.name,
                        };
                      })}
                    placeholder={'Selecciona un conductor colaborador'}
                  />
                </Form.Item>
                <Form.Item shouldUpdate>
                  {() => (
                    <Button
                      disabled={
                        !assignDriverForm.isFieldsTouched(true) ||
                        !!assignDriverForm
                          .getFieldsError()
                          .filter(({ errors }) => errors.length).length
                      }
                      htmlType={'submit'}
                      loading={updateDriverMutation[1]?.loading}
                    >
                      Asignar
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </Space>
            {drivingCompany.drivers?.length > 0 && (
              <Table
                size="small"
                loading={drivingCompanyQuery.loading}
                locale={{
                  emptyText: (
                    <Empty
                      description={'Esta empresa colaboadora no tiene usuarios'}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                pagination={{
                  pageSize: 5,
                }}
                columns={[
                  {
                    title: 'Nombre',
                    dataIndex: ['name'],
                    ellipsis: {
                      showTitle: true,
                    },
                  },
                  {
                    title: 'Email',
                    dataIndex: ['email'],
                    ellipsis: {
                      showTitle: true,
                    },
                  },
                  {
                    title: 'Phone',
                    dataIndex: ['phone'],
                    ellipsis: {
                      showTitle: true,
                    },
                  },
                  {
                    title: 'Acceso al sistema',
                    dataIndex: ['loginEnabled'],
                    ellipsis: {
                      showTitle: true,
                    },
                    render: (value) => {
                      if (value) {
                        return 'Sí';
                      }

                      return 'No';
                    },
                  },
                  {
                    key: 'actions',
                    title: 'Acciones',
                    width: 200,
                    render: (_, record) => {
                      return (
                        <Space direction={'horizontal'}>
                          <Button
                            size="small"
                            onClick={() => {
                              setSelectedDriverId(record.id);
                            }}
                          >
                            Gestionar
                          </Button>
                          <Popconfirm
                            title={
                              '¿Estás seguro que deseas desasignar este conductor de esta empresa colaboradora?'
                            }
                            onConfirm={() => onUnassignDriver(record.id)}
                            arrowPointAtCenter={true}
                            placement={'topLeft'}
                          >
                            <Button size={'small'} danger>
                              Desasignar
                            </Button>
                          </Popconfirm>
                        </Space>
                      );
                    },
                  },
                ]}
                dataSource={drivingCompany.drivers}
              />
            )}
          </Space>
          <UpsertDriverDrawer
            visible={showUpsertUserDrawer}
            driverId={selectedDriverId}
            drivingCompanyId={drivingCompany.id}
            onClose={() => {
              setShowUpsertUserDrawer(false);
              setSelectedDriverId(undefined);
            }}
          />
        </>
      )}
    </Drawer>
  );
};
